import { useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { LockKeyhole, LogOut, RefreshCw } from "lucide-react";

export default function AccessPending({ user }) {
  // Re-check session periodically so the user lands in the app the moment an admin
  // approves them — no manual reload needed.
  useEffect(() => {
    const tick = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (res.data?.status === "active" || res.data?.role === "admin") {
          localStorage.setItem("user", JSON.stringify(res.data));
          window.location.reload();
        }
      } catch {
        // ignore — pending users get 200 + status:pending
      }
    };
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // ignore
    }
    localStorage.removeItem("user");
    window.location.href = "/login";
  };

  const handleRefresh = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (res.data?.status === "active" || res.data?.role === "admin") {
        localStorage.setItem("user", JSON.stringify(res.data));
        window.location.reload();
      } else {
        // brief pulse — let user know we re-checked
        const el = document.getElementById("recheck-pulse");
        if (el) {
          el.classList.remove("opacity-0");
          el.classList.add("opacity-100");
          setTimeout(() => {
            el.classList.remove("opacity-100");
            el.classList.add("opacity-0");
          }, 1600);
        }
      }
    } catch {
      // ignore
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center" data-testid="access-pending-screen">
        <div className="mx-auto h-14 w-14 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mb-5">
          <LockKeyhole className="h-6 w-6 text-amber-600" />
        </div>
        <h1 className="text-xl font-semibold text-slate-900 mb-2" data-testid="access-pending-title">
          Access Pending Approval
        </h1>
        <p className="text-sm text-slate-600 leading-relaxed mb-6">
          Your account is awaiting admin approval. Please contact administrator.
        </p>

        {user?.email && (
          <div className="mb-6 rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-left text-xs text-slate-600">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Signed in as</div>
            <div className="font-medium text-slate-800">{user.name || user.email}</div>
            <div className="text-slate-500">{user.email}</div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <Button variant="outline" onClick={handleRefresh} data-testid="access-pending-recheck">
            <RefreshCw className="h-4 w-4 mr-1.5" />
            Check Again
          </Button>
          <Button variant="ghost" onClick={handleLogout} data-testid="access-pending-logout">
            <LogOut className="h-4 w-4 mr-1.5" />
            Sign out
          </Button>
        </div>
        <p id="recheck-pulse" className="mt-3 text-[11px] text-slate-400 opacity-0 transition-opacity duration-300">
          Still pending — admin hasn't approved yet.
        </p>
      </div>
    </div>
  );
}
